/** SASE Analytics (SASE-14, plan §7.1 rule 6): a search lives in the URL, so a
 *  view can be shared by link, and saved searches are the same query strings
 *  kept in this browser. Pure helpers: no React, no HTTP. */
import dayjs from "dayjs";

import {
  LOG_ACTIONS,
  LOG_KINDS,
  SEVERITIES,
  type ActivityFilters,
  type LogAction,
  type LogKind,
  type Severity,
  type Window,
} from "./analyticsTypes";

// ---- paths (every one inside /sase, plan §7.1 rule 3) -----------------------

export const ANALYTICS_PATH = "/sase/analytics";
export const ACTIVITY_PATH = "/sase/analytics/activity";
/** SASE-38 (P38): the scheduled and on-demand reports. */
export const REPORTS_PATH = "/sase/analytics/reports";
export const INCIDENTS_PATH = "/sase/incidents";

export function userViewPath(user: string, search = ""): string {
  return `/sase/analytics/users/${encodeURIComponent(user)}${search}`;
}

export function appViewPath(app: string, search = ""): string {
  return `/sase/analytics/apps/${encodeURIComponent(app)}${search}`;
}

/** The Activity search for these filters, keeping the time range. */
export function activityPath(filters: ActivityFilters, range?: RangeState): string {
  const params = filtersToParams(filters);
  if (range) writeRange(params, range);
  const qs = params.toString();
  return qs ? `${ACTIVITY_PATH}?${qs}` : ACTIVITY_PATH;
}

/** Incidents narrowed to these filters, keeping the time range. */
export function incidentsPath(filters: ActivityFilters, range?: RangeState): string {
  const params = filtersToParams(filters);
  if (range) writeRange(params, range);
  const qs = params.toString();
  return qs ? `${INCIDENTS_PATH}?${qs}` : INCIDENTS_PATH;
}

// ---- names customers read (plan §9) ----------------------------------------

export const KIND_LABELS: Record<LogKind, string> = {
  web: "Web",
  dns: "DNS",
  fw: "Firewall",
  threat: "Threat",
};

export const ACTION_LABELS: Record<LogAction, string> = {
  allow: "Allowed",
  inspect: "Inspected",
  bypass: "Not inspected",
  block: "Blocked",
  refuse: "Refused",
  canary: "Watched",
  safe_search: "Safe search",
  error: "Error",
  warn: "Warned",
  isolate: "Isolated",
  alert: "Alerted",
  drop: "Dropped",
  log: "Logged",
  read_only: "Read only",
};

export const ACTION_COLORS: Record<LogAction, string> = {
  allow: "success",
  inspect: "processing",
  bypass: "default",
  block: "error",
  refuse: "error",
  canary: "purple",
  safe_search: "cyan",
  error: "volcano",
  warn: "warning",
  isolate: "geekblue",
  alert: "warning",
  drop: "error",
  log: "blue",
  read_only: "gold",
};

export function isKind(value: unknown): value is LogKind {
  return typeof value === "string" && (LOG_KINDS as readonly string[]).includes(value);
}

export function isSeverity(value: unknown): value is Severity {
  return typeof value === "string" && (SEVERITIES as readonly string[]).includes(value);
}

/** How a severity filter reads, and its tag colour (the same scale as
 *  `severityLabel`). */
export const SEVERITY_LABELS: Record<Severity, { label: string; color: string }> = {
  high: { label: "High", color: "error" },
  medium: { label: "Medium", color: "warning" },
  low: { label: "Low", color: "default" },
};

/** The filter word for a stored severity number (1 high, 2 medium, 3+ low). */
export function severityWord(severity: number | null | undefined): Severity | null {
  if (severity === null || severity === undefined) return null;
  if (severity <= 1) return "high";
  if (severity === 2) return "medium";
  return "low";
}

export function isAction(value: unknown): value is LogAction {
  return typeof value === "string" && (LOG_ACTIONS as readonly string[]).includes(value);
}

export function actionLabel(action: string | null | undefined): string {
  if (!action) return "—";
  return isAction(action) ? ACTION_LABELS[action] : action;
}

export function kindLabel(kind: string | null | undefined): string {
  if (!kind) return "—";
  return isKind(kind) ? KIND_LABELS[kind] : kind;
}

/** Threat severity, 1 the most severe (the IPS convention the proposed
 *  threat record follows). */
export function severityLabel(severity: number | null | undefined): {
  label: string;
  color: string;
} {
  const word = severityWord(severity);
  return word ? SEVERITY_LABELS[word] : { label: "—", color: "default" };
}

// ---- the query builder's fields --------------------------------------------

/** A filter the query builder can add (every one an exact match). */
export type FilterField = Exclude<keyof ActivityFilters, "kinds" | "q" | "event_ids">;

/** The API's bounds on the record-id filter (A18, P31c-F4). */
export const MAX_EVENT_IDS = 20;
const MAX_EVENT_ID_LENGTH = 64;

export const FILTER_FIELDS: { key: FilterField; label: string }[] = [
  { key: "user", label: "User" },
  { key: "app", label: "App or site" },
  { key: "category", label: "Category" },
  { key: "action", label: "Decision" },
  { key: "device", label: "Device" },
  { key: "rule_id", label: "Rule" },
  { key: "pop", label: "Location" },
  { key: "txn_id", label: "Transaction" },
  { key: "severity", label: "Severity" },
  { key: "threat_category", label: "Threat category" },
  { key: "web_category", label: "Web category" },
  { key: "ai_app", label: "AI app" },
  { key: "ai_category", label: "AI app category" },
  { key: "casb_app", label: "Cloud app" },
  { key: "casb_activity", label: "App activity" },
  { key: "mcp_server", label: "MCP server" },
];

export function fieldLabel(key: FilterField): string {
  return FILTER_FIELDS.find((f) => f.key === key)?.label ?? key;
}

/** The API's length caps, so a hand-edited link cannot turn into a 422. */
const MAX_LENGTH: Record<FilterField | "q", number> = {
  user: 320,
  device: 128,
  app: 253,
  category: 64,
  action: 16,
  rule_id: 128,
  pop: 64,
  txn_id: 64,
  severity: 8,
  threat_category: 64,
  web_category: 64,
  ai_app: 128,
  ai_category: 64,
  casb_app: 64,
  casb_activity: 32,
  mcp_server: 253,
  q: 256,
};

/** The filters in a URL. Unknown kinds and decisions are dropped rather than
 *  sent (the API would refuse the whole search). */
export function filtersFromParams(params: URLSearchParams): ActivityFilters {
  const out: ActivityFilters = {};
  const kinds = params.getAll("kind").filter(isKind);
  if (kinds.length) out.kinds = Array.from(new Set(kinds));
  for (const { key } of FILTER_FIELDS) {
    const raw = params.get(key)?.trim();
    if (!raw) continue;
    const value = raw.slice(0, MAX_LENGTH[key]);
    if (key === "action") {
      if (isAction(value)) out.action = value;
    } else if (key === "severity") {
      if (isSeverity(value)) out.severity = value;
    } else {
      out[key] = value;
    }
  }
  const q = params.get("q")?.trim();
  if (q) out.q = q.slice(0, MAX_LENGTH.q);
  // record ids: whole ids only (a cut id would match nothing), at most the API's 20
  const ids = params
    .getAll("event_id")
    .map((v) => v.trim())
    .filter((v) => v && v.length <= MAX_EVENT_ID_LENGTH);
  if (ids.length) out.event_ids = Array.from(new Set(ids)).slice(0, MAX_EVENT_IDS);
  return out;
}

export function filtersToParams(filters: ActivityFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const kind of filters.kinds ?? []) params.append("kind", kind);
  for (const { key } of FILTER_FIELDS) {
    const value = filters[key];
    if (value) params.set(key, value);
  }
  if (filters.q) params.set("q", filters.q);
  for (const id of filters.event_ids ?? []) params.append("event_id", id);
  return params;
}

/** The Activity search for exactly these records, within their time window (the
 *  risk panel links a finding's evidence here). */
export function recordsPath(ids: string[], since: string, until: string): string {
  const params = filtersToParams({ event_ids: ids.slice(0, MAX_EVENT_IDS) });
  params.set("since", since);
  params.set("until", until);
  return `${ACTIVITY_PATH}?${params.toString()}`;
}

export function hasFilters(filters: ActivityFilters): boolean {
  return filtersToParams(filters).toString() !== "";
}

// ---- the time range ---------------------------------------------------------

export type RangePreset = "1h" | "24h" | "7d" | "30d";

export const RANGE_PRESETS: { key: RangePreset; label: string; ms: number }[] = [
  { key: "1h", label: "Last hour", ms: 3_600_000 },
  { key: "24h", label: "Last 24 hours", ms: 86_400_000 },
  { key: "7d", label: "Last 7 days", ms: 7 * 86_400_000 },
  { key: "30d", label: "Last 30 days", ms: 30 * 86_400_000 },
];

export const DEFAULT_RANGE: RangePreset = "24h";

/** A preset (relative to now) or a fixed window from the picker. */
export type RangeState =
  | { preset: RangePreset }
  | { preset: "custom"; since: string; until: string };

function isPreset(value: unknown): value is RangePreset {
  return RANGE_PRESETS.some((p) => p.key === value);
}

function isIsoDate(value: string | null): value is string {
  return Boolean(value) && !Number.isNaN(Date.parse(value as string));
}

export function rangeFromParams(params: URLSearchParams): RangeState {
  const since = params.get("since");
  const until = params.get("until");
  if (isIsoDate(since) && isIsoDate(until) && Date.parse(since) < Date.parse(until)) {
    return { preset: "custom", since, until };
  }
  const range = params.get("range");
  return { preset: isPreset(range) ? range : DEFAULT_RANGE };
}

/** Write the range into `params`, replacing any other range. The default
 *  preset is left out, so a plain link stays short. */
export function writeRange(params: URLSearchParams, range: RangeState): URLSearchParams {
  params.delete("range");
  params.delete("since");
  params.delete("until");
  if (range.preset === "custom") {
    params.set("since", range.since);
    params.set("until", range.until);
  } else if (range.preset !== DEFAULT_RANGE) {
    params.set("range", range.preset);
  }
  return params;
}

/** The window to ask for, computed when the read runs (a preset moves with
 *  the clock). */
export function windowOf(range: RangeState, now: number = Date.now()): Window {
  if (range.preset === "custom") return { since: range.since, until: range.until };
  const preset = RANGE_PRESETS.find((p) => p.key === range.preset) ?? RANGE_PRESETS[1]!;
  return { since: new Date(now - preset.ms).toISOString() };
}

/** `?range=…` (or `?since=…&until=…`) for a link that keeps the range, or "". */
export function rangeSearch(range: RangeState): string {
  const qs = writeRange(new URLSearchParams(), range).toString();
  return qs ? `?${qs}` : "";
}

/** A stable key for caching a range (never the computed instant). */
export function rangeKey(range: RangeState): string {
  return range.preset === "custom" ? `${range.since}..${range.until}` : range.preset;
}

// ---- saved searches (this browser only) -------------------------------------

export interface SavedSearch {
  id: string;
  name: string;
  /** The Activity search's query string, filters and range. */
  query: string;
  saved_at: string;
}

const STORAGE_PREFIX = "omnicloud.sase.savedSearches";
export const MAX_SAVED_SEARCHES = 50;

/** One list per account, so two people sharing a browser never see each
 *  other's searches. */
export function savedSearchesKey(account: string | null | undefined): string {
  return `${STORAGE_PREFIX}.${account || "anonymous"}`;
}

function isSaved(value: unknown): value is SavedSearch {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    typeof v.name === "string" &&
    typeof v.query === "string" &&
    typeof v.saved_at === "string"
  );
}

/** Storage may be missing, full, blocked or cleared (private windows,
 *  previews): every access is guarded and a failure reads as "none". */
function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function loadSavedSearches(account: string | null | undefined): SavedSearch[] {
  try {
    const raw = storage()?.getItem(savedSearchesKey(account));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isSaved).slice(0, MAX_SAVED_SEARCHES) : [];
  } catch {
    return [];
  }
}

/** Store the list; false when this browser would not keep it. */
export function storeSavedSearches(
  account: string | null | undefined,
  searches: SavedSearch[],
): boolean {
  try {
    const store = storage();
    if (!store) return false;
    store.setItem(savedSearchesKey(account), JSON.stringify(searches.slice(0, MAX_SAVED_SEARCHES)));
    return true;
  } catch {
    return false;
  }
}

/** Add (or replace, by name) a saved search; the newest comes first. */
export function withSavedSearch(
  searches: SavedSearch[],
  name: string,
  query: string,
  now: Date = new Date(),
): SavedSearch[] {
  const clean = name.trim().slice(0, 80);
  const entry: SavedSearch = {
    id: `${now.getTime().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    name: clean,
    query,
    saved_at: now.toISOString(),
  };
  return [entry, ...searches.filter((s) => s.name !== clean)].slice(0, MAX_SAVED_SEARCHES);
}

// ---- how a line reads -------------------------------------------------------

/** Where the line went: the site and path, the name looked up, or the address. */
export function destinationOf(entry: {
  kind: string;
  host?: string | null;
  sni?: string | null;
  path?: string | null;
  qname?: string | null;
  qtype?: string | null;
  dst_addr?: string | null;
  dst_port?: number | null;
}): string {
  if (entry.kind === "dns" && entry.qname)
    return entry.qtype ? `${entry.qname} (${entry.qtype})` : entry.qname;
  const site = entry.host || entry.sni;
  if (site) return entry.path ? `${site}${entry.path}` : site;
  if (entry.dst_addr)
    return entry.dst_port ? `${entry.dst_addr}:${entry.dst_port}` : entry.dst_addr;
  return "—";
}

/** The app a line belongs to, for the App view: the app, else the site. */
export function appOf(entry: {
  app?: string | null;
  host?: string | null;
  sni?: string | null;
  qname?: string | null;
}): string | null {
  return entry.app || entry.host || entry.sni || entry.qname || null;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
}

/** A line's time, local, to the second. */
export function when(timestamp: string): string {
  const t = dayjs(timestamp);
  return t.isValid() ? t.format("YYYY-MM-DD HH:mm:ss") : timestamp;
}

/** The HTTP status of a failed read, and the server's reason. */
export function readFailure(error: unknown): { status: number | null; detail: string | null } {
  const response = (error as { response?: { status?: unknown; data?: unknown } } | null)?.response;
  const status = typeof response?.status === "number" ? response.status : null;
  const data = response?.data as { detail?: unknown } | undefined;
  const detail = typeof data?.detail === "string" ? data.detail : null;
  return { status, detail };
}
